import "./OngoingMonitoringSection.css";

import { RoundedPlusSignIcon, TrashDark } from "../../../../assets";
import { ProjectDatesConstants } from "../../../../constants";
import { ProjectPhaseStatus, SelectData } from "../../../../models";
import { Button, Column, DatePicker, KanaTooltip, Row, Select } from "../../../../widget";
import { DateSchemaFields } from "../DateSchemaFields";
import { EditDatesPanelBanner } from "../EditDatesPanelBanner";
import { DateSectionProps } from "../models";

interface OngoingMonitoringSectionProps extends DateSectionProps {
  subsequentFrequencySelectData: SelectData;
  addedCycleCount: number;
  handleAddCycle: () => void;
  handleRemoveCycle: (cycleUuid: string) => void;
}
const CYCLE_START_LABEL = "Cycle start date";
const CYCLE_END_LABEL = "Cycle end date";
const CYCLE_ASSESSMENT_DATE = "Assessment date";

/** The monitoring frequency Select and cycle repeater are rendered by hand; the schema-driven fields cover the rest. */
const BESPOKE_ONGOING_MONITORING_KEYS = ["subsequentFrequency", "finalAssessmentDate"];

export const OngoingMonitoringSection = ({
  dates,
  subsequentFrequencySelectData,
  addedCycleCount,
  handleAddCycle,
  handleRemoveCycle,
  handleUpdateProjectDates,
  hasDateError,
  schema,
}: OngoingMonitoringSectionProps): JSX.Element => {
  return (
    <div className="OngoingMonitoringSection">
      <Row spacingV="m">
        <Column span={12}>
          <p className="body01">Ongoing monitoring</p>
        </Column>
      </Row>
      <Row spacingV="l">
        <Column span={12}>
          <p className="body1">
            Enter key dates to help structure your workflow and align with your broader roadmap. These can be either
            confirmed or estimated, and can be updated as the project develops.
          </p>
        </Column>
      </Row>
      <DateSchemaFields
        schemaProperties={schema?.ongoingMonitoring.properties}
        values={dates.ongoingMonitoring}
        keyPrefix="ongoingMonitoring"
        excludeKeys={BESPOKE_ONGOING_MONITORING_KEYS}
        hasError={hasDateError}
        onChange={handleUpdateProjectDates}
      />
      {(schema?.ongoingMonitoring.properties.subsequentFrequency !== undefined ||
        schema?.ongoingMonitoring.properties.finalAssessmentDate !== undefined) && (
        <Row spacingV="l">
          <Column span={4}>
            <Select
              label="Monitoring frequency"
              value={
                dates.ongoingMonitoring?.subsequentFrequency !== undefined
                  ? (subsequentFrequencySelectData.filter(
                      (e) => e.key === dates.ongoingMonitoring?.subsequentFrequency
                    )[0]?.value as string)
                  : undefined
              }
              data={subsequentFrequencySelectData}
              onChange={(key) => handleUpdateProjectDates(+key, "ongoingMonitoring.subsequentFrequency")}
              tooltip={
                <KanaTooltip
                  tooltipHeader=""
                  tooltipText={ProjectDatesConstants.ONGOING_MONITORING_SUBSEQUENT_FREQUENCY_TOOLTIP_TEXT}
                />
              }
            />
          </Column>
          <Column offset="1_half" span={4}>
            <DatePicker
              label="Final assessment date"
              value={dates.ongoingMonitoring?.finalAssessmentDate}
              onChange={(date) => handleUpdateProjectDates(date, "ongoingMonitoring.finalAssessmentDate")}
              hasError={hasDateError("ongoingMonitoring.finalAssessmentDate")}
              tooltip={
                <KanaTooltip
                  tooltipHeader=""
                  tooltipText={ProjectDatesConstants.ONGOING_MONITORING_FINAL_ASSESSMENT_DATE_TOOLTIP_TEXT}
                  hasError={hasDateError("ongoingMonitoring.finalAssessmentDate")}
                />
              }
            />
          </Column>
        </Row>
      )}
      {dates.ongoingMonitoring.cycles &&
        dates.ongoingMonitoring.cycles.map((cycle, index) => (
          <div key={cycle.uuid}>
            <Row spacingV="l">
              <Column span={12}>
                <p className="body01">Cycle {index + 1}</p>
              </Column>
            </Row>
            <Row spacingV="l">
              <Column span={4}>
                <DatePicker
                  label={CYCLE_START_LABEL}
                  value={cycle.start}
                  onChange={(date) => handleUpdateProjectDates(date, `ongoingMonitoring.cycles[${index}].start`)}
                  hasError={hasDateError(`ongoingMonitoring.cycles[${index}].start`)}
                  tooltip={
                    <KanaTooltip
                      tooltipHeader=""
                      tooltipText={ProjectDatesConstants.ONGOING_MONITORING_CYCLE_START_TOOLTIP_TEXT}
                      hasError={hasDateError(`ongoingMonitoring.cycles[${index}].start`)}
                    />
                  }
                />
              </Column>
              <Column offset="1_half" span={4}>
                <DatePicker
                  label={CYCLE_END_LABEL}
                  value={cycle.end}
                  onChange={(date) => handleUpdateProjectDates(date, `ongoingMonitoring.cycles[${index}].end`)}
                  hasError={hasDateError(`ongoingMonitoring.cycles[${index}].end`)}
                  tooltip={
                    <KanaTooltip
                      tooltipHeader=""
                      tooltipText={ProjectDatesConstants.ONGOING_MONITORING_CYCLE_END_TOOLTIP_TEXT}
                      hasError={hasDateError(`ongoingMonitoring.cycles[${index}].end`)}
                    />
                  }
                />
              </Column>
            </Row>
            <Row spacingV="l" align="center">
              <Column span={4}>
                <DatePicker
                  label={CYCLE_ASSESSMENT_DATE}
                  value={cycle.assessmentDate}
                  onChange={(date) =>
                    handleUpdateProjectDates(date, `ongoingMonitoring.cycles[${index}].assessmentDate`)
                  }
                  hasError={hasDateError(`ongoingMonitoring.cycles[${index}].assessmentDate`)}
                  tooltip={
                    <KanaTooltip
                      tooltipHeader=""
                      tooltipText={ProjectDatesConstants.ONGOING_MONITORING_CYCLE_ASSESSMENT_TOOLTIP_TEXT}
                      hasError={hasDateError(`ongoingMonitoring.cycles[${index}].assessmentDate`)}
                    />
                  }
                />
              </Column>
              {dates.ongoingMonitoring.cycles.length > 1 &&
                index === dates.ongoingMonitoring.cycles.length - 1 &&
                addedCycleCount > 0 && (
                  <Column offset="1_half" span={4} hasNoPadding>
                    <Button
                      text="Remove cycle"
                      variant="reversedPrimary"
                      iconPosition="left"
                      hasNoPadding
                      icon={<TrashDark className="TrashIcon" />}
                      onClick={() => handleRemoveCycle(cycle.uuid)}
                    />
                  </Column>
                )}
            </Row>
          </div>
        ))}
      {dates.ongoingMonitoring.cycles &&
        dates.ongoingMonitoring.cycles.length < 15 &&
        (dates.ongoingMonitoring.cycles.length === 0 ||
          (dates.ongoingMonitoring.cycles[dates.ongoingMonitoring.cycles.length - 1]?.start !== undefined &&
            dates.ongoingMonitoring.cycles[dates.ongoingMonitoring.cycles.length - 1]?.end !== undefined)) && (
          <Row spacingV="xl">
            <Column span={12}>
              <Button
                text="Add another cycle"
                icon={<RoundedPlusSignIcon className="RoundedPlusSignIcon" />}
                variant="reversedPrimary"
                iconPosition="left"
                hasNoPadding
                onClick={handleAddCycle}
              />
            </Column>
          </Row>
        )}
      <EditDatesPanelBanner
        phaseStatus={dates.ongoingMonitoring.overallStatus as ProjectPhaseStatus}
        phaseEndDate={dates.ongoingMonitoring.overallEnd}
      />
    </div>
  );
};
